import {
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Stack,
    Typography,
} from '@mui/material';

import {
    BooleanField,
    BooleanInput,
    Create,
    CreateButton,
    DateField,
    Datagrid,
    Edit,
    EditButton,
    FilterButton,
    FunctionField,
    List,
    NumberInput,
    required,
    SelectInput,
    Show,
    ShowButton,
    SimpleForm,
    SimpleShowLayout,
    TextField,
    TextInput,
    TopToolbar,
    useDataProvider,
    useNotify,
    useRecordContext,
    useRefresh,
} from 'react-admin';

import { ADMIN_USERS_RESOURCE, restoreAdminUser } from '../admin/adminUserDataProvider';

const booleanFilterChoices = [
    { id: 'true', name: 'Yes' },
    { id: 'false', name: 'No' },
];

const authByChoices = [
    { id: 'EmailOrMobile', name: 'Email or Mobile' },
    { id: 'Google', name: 'Google' },
    { id: 'Apple', name: 'Apple' },
];

const examNameChoices = [
    { id: 'Core', name: 'Core' },
    { id: 'Academic', name: 'Academic' },
];

const userListFilters = [
    <TextInput key="search" source="search" label="Search" alwaysOn resettable />,
    <SelectInput key="isAdmin" source="isAdmin" label="Admin" choices={booleanFilterChoices} alwaysOn emptyText="All" />,
    <SelectInput key="isActivated" source="isActivated" label="Activated" choices={booleanFilterChoices} alwaysOn emptyText="All" />,
    <SelectInput key="isDeleted" source="isDeleted" label="Deleted" choices={booleanFilterChoices} emptyText="All" />,
    <SelectInput key="authBy" source="authBy" label="Auth By" choices={authByChoices} emptyText="All" />,
    <SelectInput key="examName" source="examName" label="Exam" choices={examNameChoices} emptyText="All" />,
    <SelectInput key="verified" source="verified" label="Verified" choices={booleanFilterChoices} emptyText="All" />,
    <SelectInput key="emailVerified" source="emailVerified" label="Email Verified" choices={booleanFilterChoices} emptyText="All" />,
    <SelectInput key="mobileVerified" source="mobileVerified" label="Mobile Verified" choices={booleanFilterChoices} emptyText="All" />,
    <SelectInput key="isProfileComplete" source="isProfileComplete" label="Profile Complete" choices={booleanFilterChoices} emptyText="All" />,
    <SelectInput key="hasProfileImage" source="hasProfileImage" label="Has Photo" choices={booleanFilterChoices} emptyText="All" />,
    <SelectInput key="hasPendingProfileImage" source="hasPendingProfileImage" label="Pending Photo" choices={booleanFilterChoices} emptyText="All" />,
    <TextInput key="country" source="country" label="Country" resettable />,
    <TextInput key="language" source="language" label="Language" resettable />,
    <TextInput key="createdVia" source="createdVia" label="Created Via" resettable />,
    <TextInput key="adminTag" source="adminTag" label="Admin Tag" resettable />,
    <TextInput key="createdFromUtc" source="createdFromUtc" label="Created From" type="date" />,
    <TextInput key="createdToUtc" source="createdToUtc" label="Created To" type="date" />,
    <TextInput key="updatedFromUtc" source="updatedFromUtc" label="Updated From" type="date" />,
    <TextInput key="updatedToUtc" source="updatedToUtc" label="Updated To" type="date" />,
];

const listActions = (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
    </TopToolbar>
);

function SectionCard({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
    return (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
                    {title}
                </Typography>
                {description ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {description}
                    </Typography>
                ) : null}
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1.5}>{children}</Stack>
            </CardContent>
        </Card>
    );
}

function AdminUserRestoreButton() {
    const record = useRecordContext<any>();
    const notify = useNotify();
    const refresh = useRefresh();

    if (!record || record.isDeleted !== true) {
        return null;
    }

    const handleRestore = async () => {
        if (!window.confirm(`Restore user ${record.fullName || record.email || record.userId}?`)) {
            return;
        }

        try {
            await restoreAdminUser(record.id);
            notify('User restored', { type: 'success' });
            refresh();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to restore user', { type: 'error' });
        }
    };

    return (
        <Button size="small" variant="outlined" color="success" onClick={handleRestore}>
            Restore
        </Button>
    );
}

function AdminUserRowActions() {
    const record = useRecordContext<any>();

    if (!record) {
        return null;
    }

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <ShowButton />
            <EditButton />
            {record.isDeleted === true ? <AdminUserRestoreButton /> : <DeleteAction />}
        </Stack>
    );
}

function DeleteAction() {
    const record = useRecordContext<any>();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    if (!record || record.isDeleted === true) {
        return null;
    }

    const handleDelete = async () => {
        if (!window.confirm(`Delete user ${record.fullName || record.email || record.userId}?`)) {
            return;
        }

        try {
            await dataProvider.delete(ADMIN_USERS_RESOURCE, { id: record.id, previousData: record });
            notify('User deleted', { type: 'success' });
            refresh();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to delete user', { type: 'error' });
        }
    };

    return (
        <Button size="small" color="error" variant="text" onClick={handleDelete}>
            Delete
        </Button>
    );
}

function AdminUserFormSections({ isCreate }: { isCreate: boolean }) {
    return (
        <>
            <SectionCard title="Identity" description="Primary identifiers and authentication settings.">
                <TextInput source="firstName" label="First Name" fullWidth />
                <TextInput source="lastName" label="Last Name" fullWidth />
                <TextInput source="email" label="Email" fullWidth />
                <TextInput source="mobile" label="Mobile" fullWidth />
                <SelectInput source="authBy" label="Auth By" choices={authByChoices} validate={required()} fullWidth />
                <TextInput
                    source="adminSetPassword"
                    label={isCreate ? 'Admin Set Password' : 'Reset Password'}
                    type="password"
                    helperText={isCreate ? 'Required when Auth By is Email or Mobile.' : 'Leave empty to keep the existing password.'}
                    fullWidth
                />
            </SectionCard>

            <SectionCard title="Status" description="Administrative state and verification flags.">
                <BooleanInput source="emailVerified" label="Email Verified" />
                <BooleanInput source="mobileVerified" label="Mobile Verified" />
                <BooleanInput source="verified" label="Verified" />
                <BooleanInput source="isActivated" label="Activated" />
                <BooleanInput source="isAdmin" label="Admin" />
                <BooleanInput source="isProfileComplete" label="Profile Complete" />
                <SelectInput source="examName" label="Exam Name" choices={examNameChoices} emptyText="None" fullWidth />
                <TextInput source="createdVia" label="Created Via" fullWidth />
                <TextInput source="cfCountry" label="CF Country" fullWidth />
            </SectionCard>

            <SectionCard title="Profile" description="General profile data exposed to admins.">
                <TextInput source="language" label="Language" fullWidth />
                <TextInput source="country" label="Country" fullWidth />
                <TextInput source="state" label="State" fullWidth />
                <TextInput source="gender" label="Gender" fullWidth />
                <TextInput source="dateOfBirth" label="Date Of Birth" type="datetime-local" fullWidth />
                <TextInput source="education" label="Education" fullWidth />
                <TextInput source="adminTags" label="Admin Tags" helperText="Comma-separated values" fullWidth />
            </SectionCard>

            <SectionCard title="Location" description="Address and geo fields.">
                <TextInput source="livesIn" label="Lives In" fullWidth />
                <TextInput source="placeId" label="Place Id" fullWidth />
                <NumberInput source="lat" label="Latitude" fullWidth />
                <NumberInput source="lon" label="Longitude" fullWidth />
                <TextInput source="postalNumber" label="Postal Number" fullWidth />
                <TextInput source="street" label="Street" fullWidth />
                <TextInput source="localArea" label="Local Area" fullWidth />
                <TextInput source="district" label="District" fullWidth />
                <TextInput source="taluka" label="Taluka" fullWidth />
            </SectionCard>

            <SectionCard title="Media" description="Current and pending profile image locations.">
                <TextInput source="profileImageUrl" label="Profile Image URL" fullWidth />
                <TextInput source="profileImageUrlPending" label="Pending Image URL" fullWidth />
                <TextInput source="mobileCode" label="Mobile Code" fullWidth />
            </SectionCard>
        </>
    );
}

export const AdminUserList = () => (
    <List actions={listActions} filters={userListFilters} perPage={25} sort={{ field: 'createdAt', order: 'DESC' }}>
        <Datagrid rowClick="show" bulkActionButtons={false}>
            <TextField source="userId" label="User Id" />
            <FunctionField
                label="Name"
                render={(record: any) => record.fullName || [record.firstName, record.lastName].filter(Boolean).join(' ') || '-'}
            />
            <TextField source="email" />
            <TextField source="mobile" />
            <TextField source="authBy" label="Auth" />
            <TextField source="examName" label="Exam" />
            <BooleanField source="isAdmin" label="Admin" />
            <BooleanField source="isActivated" label="Active" />
            <BooleanField source="verified" label="Verified" />
            <BooleanField source="isProfileComplete" label="Profile" />
            <TextField source="country" />
            <FunctionField label="Tags" render={(record: any) => record.adminTagsText || '-'} />
            <FunctionField
                label="State"
                render={(record: any) =>
                    record.isDeleted ? <Chip size="small" color="warning" label="Deleted" /> : <Chip size="small" color="success" label="Live" />
                }
            />
            <DateField source="createdAt" showTime />
            <DateField source="updatedAt" showTime />
            <FunctionField label="Actions" render={() => <AdminUserRowActions />} />
        </Datagrid>
    </List>
);

export const AdminUserShow = () => (
    <Show>
        <SimpleShowLayout>
            <TextField source="userId" label="User Id" />
            <FunctionField
                label="Full Name"
                render={(record: any) => record.fullName || [record.firstName, record.lastName].filter(Boolean).join(' ') || '-'}
            />
            <TextField source="firstName" />
            <TextField source="lastName" />
            <TextField source="email" />
            <BooleanField source="emailVerified" label="Email Verified" />
            <TextField source="mobile" />
            <BooleanField source="mobileVerified" label="Mobile Verified" />
            <BooleanField source="verified" label="Verified" />
            <TextField source="authBy" label="Auth By" />
            <BooleanField source="isActivated" label="Activated" />
            <BooleanField source="isAdmin" label="Admin" />
            <BooleanField source="isDeleted" label="Deleted" />
            <BooleanField source="isProfileComplete" label="Profile Complete" />
            <TextField source="examName" label="Exam" />
            <TextField source="language" />
            <TextField source="country" />
            <TextField source="state" />
            <TextField source="createdVia" label="Created Via" />
            <FunctionField label="Admin Tags" render={(record: any) => record.adminTagsText || '-'} />
            <TextField source="profileImageUrl" label="Profile Image URL" />
            <TextField source="profileImageUrlPending" label="Pending Image URL" />
            <TextField source="livesIn" label="Lives In" />
            <TextField source="placeId" label="Place Id" />
            <TextField source="lat" label="Latitude" />
            <TextField source="lon" label="Longitude" />
            <TextField source="dateOfBirth" label="Date Of Birth" />
            <TextField source="gender" />
            <TextField source="postalNumber" label="Postal Number" />
            <TextField source="education" />
            <TextField source="mobileCode" label="Mobile Code" />
            <TextField source="street" />
            <TextField source="localArea" label="Local Area" />
            <TextField source="district" />
            <TextField source="taluka" />
            <TextField source="cfCountry" label="CF Country" />
            <DateField source="createdAt" showTime />
            <DateField source="updatedAt" showTime />
            <AdminUserRestoreButton />
        </SimpleShowLayout>
    </Show>
);

export const AdminUserCreate = () => (
    <Create resource={ADMIN_USERS_RESOURCE}>
        <SimpleForm>
            <AdminUserFormSections isCreate />
        </SimpleForm>
    </Create>
);

export const AdminUserEdit = () => (
    <Edit resource={ADMIN_USERS_RESOURCE}>
        <SimpleForm>
            <TextInput source="userId" label="User Id" disabled fullWidth />
            <AdminUserRestoreButton />
            <AdminUserFormSections isCreate={false} />
            <TextInput source="createdAt" label="Created At" disabled fullWidth />
            <TextInput source="updatedAt" label="Updated At" disabled fullWidth />
        </SimpleForm>
    </Edit>
);